// src/components/DeploymentSearch.js
import React, { useEffect, useState, useMemo } from "react";
import AddDeploymentForm from "./AddDeploymentForm";
import ViewDeploymentForm from "./ViewDeploymentForm";
import EditDeploymentForm from "./EditDeploymentForm";
import editIcon from "../assets/icons/EditIcon.svg";
import viewIcon from "../assets/icons/ViewIcon.svg";

export default function DeploymentSearch() {
  const [deployments, setDeployments] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [deploymentTypes, setDeploymentTypes] = useState([]);
  const [searchText, setSearchText] = useState("");
  
  // --- NEW FILTERS ---
  const [completedByFilter, setCompletedByFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  // -------------------

  const [loading, setLoading] = useState(false);
  const [viewId, setViewId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [secureData, setSecureData] = useState(null);
  const [authError, setAuthError] = useState("");

  const currentUserId = useMemo(() => {
    const payload = secureData?.admin || secureData?.auth || null;
    return payload?.user_id ?? payload?.admin_id ?? null;
  }, [secureData]);

  // Extract unique names for the "Completed By" dropdown
  const completedByOptions = useMemo(() => {
    const names = deployments
      .map((d) => d.completed_by_name)
      .filter((name) => name);
    return [...new Set(names)];
  }, [deployments]);

  const fetchDeployments = () => {
    setLoading(true);
    setError("");

    const params = new URLSearchParams();
    if (statusFilter) params.append("status", statusFilter);
    if (vendorFilter) params.append("vendor_id", vendorFilter);
    if (typeFilter) params.append("deploymenttype_id", typeFilter);
    if (searchText) params.append("q", searchText);
    
    // --- ADD NEW PARAMS TO API CALL ---
    if (completedByFilter) params.append("completed_by_name", completedByFilter);
    if (dateFilter) params.append("completed_at", dateFilter);
    // ----------------------------------

    const qs = params.toString() ? `?${params.toString()}` : "";

    fetch(`/api/deployments${qs}`)
      .then((r) => r.json())
      .then((data) => setDeployments(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("Error fetching deployments", err);
        setError("Failed to load deployments.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDeployments();
  }, [statusFilter, vendorFilter, typeFilter, completedByFilter, dateFilter]);

  useEffect(() => {
    fetch("/api/deployment-types")
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) setDeploymentTypes(data);
      })
      .catch((err) => {
        console.error("Error fetching deployment types", err);
      });
  }, []);

  useEffect(() => {
    fetch("/api/secure-endpoint", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((r) => {
        if (r.status === 401 || r.status === 403) {
          throw new Error("Not authorized — please log in again.");
        }
        if (!r.ok) throw new Error("Failed to fetch secure data");
        return r.json();
      })
      .then(setSecureData)
      .catch((e) => {
        console.error("Error fetching secure data:", e);
        setAuthError(e.message);
      });
  }, []);

  const onDeploymentCreated = () => {
    setShowAddModal(false);
    fetchDeployments();
  };

  const handleEditSaved = () => {
    setEditId(null);
    fetchDeployments();
  };

  // ===== TEMP: bulk Samsung approval (remove with the button below) =====
  const [approvingAll, setApprovingAll] = useState(false);
  const handleApproveAll = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      alert("You appear to be logged out. Please log in again.");
      return;
    }
    if (!window.confirm("Mark ALL deployments as Samsung Approved? This skips the approval email for every deployment.")) return;

    setApprovingAll(true);
    try {
      const res = await fetch("/api/deployments/temp-approve-all", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || "Failed to approve deployments.");
      alert(`${data.updated} deployment${data.updated === 1 ? "" : "s"} marked as Samsung Approved.`);
      fetchDeployments();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setApprovingAll(false);
    }
  };
  // ===== END TEMP =====

  return (
    <div className="user-list-container">
      <div className="deployment-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Deployment Jobs</h2>
        <div className="button-container" style={{ margin: 0 }}>
          {/* TEMP: bulk Samsung approval */}
          <button
            className="add-location-button"
            onClick={handleApproveAll}
            disabled={!currentUserId || approvingAll}
            style={{ marginRight: '10px', background: '#16a34a' }}
          >
            {approvingAll ? "Approving…" : "Samsung Approve All"}
          </button>
          <button
            className="add-location-button"
            onClick={() => setShowAddModal(true)}
            disabled={!currentUserId}
          >
            + New Deployment
          </button>
        </div>
      </div>

      {authError && (
        <p className="error-message">
          {authError} <a href="/login" style={{ marginLeft: '5px' }}>Login</a>
        </p>
      )}

      {/* Filters Section */}
      <div className="search-section" style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search by location / type"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && fetchDeployments()}
          style={{ width: '220px', margin: 0 }}
        />

        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ width: 'auto', margin: 0 }}>
          <option value="">All Deployment Types</option>
          {deploymentTypes.map((dt) => (
            <option key={dt.deploymenttype_id} value={dt.deploymenttype_id}>{dt.deploymenttype_name}</option>
          ))}
        </select>

        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 'auto', margin: 0 }}>
          <option value="">All Status</option>
          <option value="IN_PROGRESS">In Progress</option>
          <option value="COMPLETED">Completed</option>
          <option value="CANCELLED">Cancelled</option>
        </select>

        <select value={vendorFilter} onChange={(e) => setVendorFilter(e.target.value)} style={{ width: 'auto', margin: 0 }}>
          <option value="">All Vendors</option>
          <option value="1">Spireworks</option>
          <option value="2">Bleu Sea</option>
          <option value="3">Wei Creative</option>
        </select>

        <select 
          value={completedByFilter} 
          onChange={(e) => setCompletedByFilter(e.target.value)}
          style={{ width: 'auto', margin: 0 }}
        >
          <option value="">All Completed By</option>
          {completedByOptions.map(name => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>

        <input
          type="date"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          style={{ width: 'auto', margin: 0, padding: '8px' }}
        />

        <button className="add-location-button" onClick={fetchDeployments} style={{ padding: '10px 20px' }}>
          Refresh
        </button>
      </div>
      
      {error && <p className="error-message">{error}</p>}
      {loading && <p>Loading…</p>}

      <div className="UserList-container">
        <table>
          <thead>
            <tr>
              <th>Location</th>
              <th>Deployment Type</th>
              <th>Status</th>
              <th>Vendor</th>
              <th>Completed By</th>
              <th>Date / Time</th>
              <th style={{ textAlign: 'center' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {deployments.length === 0 && !loading && (
              <tr>
                <td colSpan="7" style={{ textAlign: "center", padding: '20px' }}>
                  No deployment jobs found.
                </td>
              </tr>
            )}

            {deployments.map((job) => (
              <tr key={job.deployment_id}>
                <td>{job.deployment_location_name || "-"}</td>
                <td>{job.deployment_type || "-"}</td>
                <td>
                  {job.status === "COMPLETED"
                    ? "Completed"
                    : job.status === "CANCELLED"
                    ? "Cancelled"
                    : "In Progress"}
                </td>
                <td>{job.vendor_name || "-"}</td>
                <td>{job.completed_by_name || "-"}</td>
                <td>
                  {job.completed_at
                    ? new Date(job.completed_at).toLocaleString()
                    : "-"}
                </td>
                <td className="action-cell">
                  <div className="action-wrapper">
                    {/* View button opens ViewDeploymentForm */}
                    <button
                      className="icon-action-btn"
                      onClick={() => setViewId(job.deployment_id)}
                      title="View Details"
                    >
                      <img src={viewIcon} alt="View" className="table-icon" />
                    </button>

                    {job.status !== "COMPLETED" && job.status !== "CANCELLED" && (
                      <button
                        className="icon-action-btn"
                        onClick={() => setEditId(job.deployment_id)}
                        disabled={!currentUserId}
                        title="Edit Deployment"
                      >
                        <img src={editIcon} alt="Edit" className="table-icon" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {showAddModal && currentUserId && (
        <AddDeploymentForm onClose={() => setShowAddModal(false)} onCreated={onDeploymentCreated} userId={currentUserId} />
      )}
      
      {/* View button opens ViewDeploymentForm which contains the Upload Files button */}
      {viewId && <ViewDeploymentForm deploymentId={viewId} onClose={() => setViewId(null)} />}
      
      {editId && currentUserId && (
        <EditDeploymentForm deploymentId={editId} userId={currentUserId} onClose={() => setEditId(null)} onSaved={handleEditSaved} />
      )}
    </div>
  );
}